/**
 * MemoForm - Compact inline form for creating and editing memos (notes and
 * tasks — `entityType` picks the family).
 *
 * Layout:
 *  - Title row: borderless semibold title input (tasks always; notes only
 *    when a legacy title exists) + the privacy / owner controls cluster and
 *    the optional close button.
 *  - Plate content editor.
 *  - Metadata bar with the date picker and stage picker — tasks only. Notes
 *    are freeform content: no title, no due, no stage.
 *  - Inline (record-slideout) variant only: the caller's attribution row, with
 *    the same controls cluster at its end instead of in the title row.
 *  - Footer with cancel/save and the ⌘ Enter hint — non-inline only.
 *
 * This file owns MemoForm's state, then hands it all to `MemoFormLayout` for
 * composition. The pieces live beside it: `MemoFormLayout` (which itself
 * composes `MemoFormTitleRow`, `MemoFormControls` — with `MemoPrivacyToggle`
 * + `MemoOwnerPicker` —, `MemoAttributionRow`, `MemoFormFooter`, and
 * `TaskFormMetadataBar` for tasks); `useMemoFormState` for the value state
 * and note-identity reset, `useMemoFormOrchestration` for the field-level
 * derivations (stage, owner, labels) and their input handlers,
 * `useMemoFormSubmit` for the single `submitNow` persist path (shared by
 * Save, ⌘ Enter and the inline autosave) plus the dirty/valid derivations
 * and submit/keydown handlers that gate it, `useMemoFormAutofocus` for the
 * on-open focus, and `memoFormValues` for the pure derivations (due,
 * emptiness, dirtiness) — see those files for their docs.
 */

import { useRef } from 'react';

import { useOrgTimeFormat } from '@/lib/time';

import type { MemoFormProps } from './MemoForm.types';
import { MemoFormControls } from './MemoFormControls';
import { MemoFormLayout } from './MemoFormLayout';
import { useMemoFormAutofocus } from './useMemoFormAutofocus';
import { useMemoFormOrchestration } from './useMemoFormOrchestration';
import { useMemoFormState } from './useMemoFormState';
import { useMemoFormSubmit } from './useMemoFormSubmit';

export type { MemoFormInitialValues } from './MemoForm.types';

export function MemoForm({
  mode,
  note,
  users,
  currentUserEntityId,
  orgId,
  onSubmit,
  onCancel,
  showCloseButton = false,
  isSubmitting = false,
  error,
  slotAfterContent,
  initialValues,
  size = 'default',
  variant = 'default',
  attribution,
  initialCaretPoint,
  initialTitleCaretOffset,
  onDirtyChange,
  onDueChange,
  entityType = 'ahoy_note',
}: MemoFormProps) {
  const isInline = variant === 'inline';
  // Notes are freeform content only: no title, no due date/time, no stage.
  // Those belong to tasks. For notes the form ignores every create-mode
  // prefill for them and never renders their controls; a LEGACY note that
  // already carries a title/date/stage keeps those values untouched on save
  // (they're seeded from `note` below and round-trip unchanged) and still
  // shows its title.
  const isNote = entityType === 'ahoy_note';
  // The inline variant autosaves in the background, so `isSubmitting` flips true
  // on every save. Disabling / making-readonly the editor and controls on that
  // signal would flash the field read-only and drop focus mid-type — autosave is
  // meant to be invisible. So the inline variant never locks its controls; the
  // modal (explicit Save) still disables during submit.
  const submitLock = isInline ? false : isSubmitting;
  const { hourCycle } = useOrgTimeFormat();

  const {
    title,
    setTitle,
    content,
    setContent,
    date,
    setDate,
    time,
    setTime,
    ownerEntityId,
    setOwnerEntityId,
    stagePickerOverride,
    setStagePickerOverride,
    privacy,
    setPrivacy,
  } = useMemoFormState({
    mode,
    note,
    isNote,
    isInline,
    currentUserEntityId,
    initialTitle: initialValues?.title,
    initialContent: initialValues?.content,
    initialDate: initialValues?.date,
    initialPrivacy: initialValues?.privacy,
  });

  const titleInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Field-level derivations (effective stage, owner, kind-driven copy,
  // title-row visibility) and the input handlers that feed them — see
  // useMemoFormOrchestration for the mechanics.
  const {
    effectiveStageAssociation,
    owner,
    canTogglePrivacy,
    noteKind,
    submitLabel,
    titlePlaceholder,
    contentPlaceholder,
    showTitleInput,
    showTitleRow,
    handleTitleChange,
    handleDateChange,
    handleTimeChange,
    handlePrivacyChange,
    handleStagePickerChange,
  } = useMemoFormOrchestration({
    mode,
    note,
    isNote,
    isInline,
    currentUserEntityId,
    users,
    orgId,
    ownerEntityId,
    setOwnerEntityId,
    setTitle,
    setDate,
    setTime,
    setPrivacy,
    date,
    time,
    stagePickerOverride,
    setStagePickerOverride,
    initialStageEntityId: initialValues?.stageEntityId,
    onDueChange,
  });

  // Dirtiness/validity, the single `submitNow` persist path shared by Save,
  // ⌘ Enter and the inline autosave, and the submit/keydown handlers that
  // gate it — see useMemoFormSubmit for the mechanics.
  const { isValid, handleSubmit, handleKeyDown } = useMemoFormSubmit({
    mode,
    note,
    entityType,
    title,
    content,
    date,
    time,
    ownerEntityId,
    privacy,
    stagePickerOverride,
    effectiveStageAssociation,
    onSubmit,
    onCancel,
    onDirtyChange,
    isInline,
    isSubmitting,
    formRef,
  });

  useMemoFormAutofocus({
    isInline,
    initialCaretPoint,
    initialTitleCaretOffset,
    showTitleInput,
    titleInputRef,
    formRef,
  });
  // On-open focus for the BODY editor. The autofocus hook only knows how to
  // focus the title input; when there is no title input (a titleless note
  // opened via the header/edit button) and no click-caret to place, nothing
  // would receive focus and the caret would sit on the trigger. In that case
  // focus the body editor instead. When a caret point exists, the editor's own
  // click-to-edit caret placement already focuses the body, so leave it off to
  // avoid fighting it.
  const autoFocusBody = !showTitleInput && !initialCaretPoint;

  const controls = (
    <MemoFormControls
      privacy={privacy}
      canTogglePrivacy={canTogglePrivacy}
      onPrivacyChange={handlePrivacyChange}
      submitLock={submitLock}
      owner={owner}
    />
  );

  return (
    <MemoFormLayout
      formRef={formRef}
      onSubmit={handleSubmit}
      isInline={isInline}
      size={size}
      showTitleRow={showTitleRow}
      titleInputRef={titleInputRef}
      showTitleInput={showTitleInput}
      controls={controls}
      title={title}
      onTitleChange={handleTitleChange}
      onKeyDown={handleKeyDown}
      submitLock={submitLock}
      titlePlaceholder={titlePlaceholder}
      mode={mode}
      showCloseButton={showCloseButton}
      onCancel={onCancel}
      content={content}
      onContentChange={setContent}
      contentPlaceholder={contentPlaceholder}
      autoFocusBody={autoFocusBody}
      initialCaretPoint={initialCaretPoint}
      orgId={orgId}
      isNote={isNote}
      date={date}
      time={time}
      onDateChange={handleDateChange}
      onTimeChange={handleTimeChange}
      hourCycle={hourCycle}
      effectiveStageAssociation={effectiveStageAssociation}
      onStagePickerChange={handleStagePickerChange}
      attribution={attribution}
      slotAfterContent={slotAfterContent}
      error={error}
      noteKind={noteKind}
      submitLabel={submitLabel}
      isValid={isValid}
      isSubmitting={isSubmitting}
    />
  );
}
